import React, { useState } from 'react';
import {
  Backdrop,
  DotProgress,
  Typography,
  Box,
  MuiBox,
  Button,
  FlexBox,
  Selector,
  Grid,
  SelectDropdown,
  color
} from '@kis-technologies/kis-ui';
import { useSelector } from 'react-redux';
import { useActions } from 'hooks/useActions';
import {
  venueColumns,
  clientIntegrationColumns,
  kioskColumns,
  configurationColumns,
  hardwareColumns,
  integrationColumns,
  ticketTemplateColumns,
  areaColumns,
  kioskHardwareColumns,
  kioskTicketTemplateColumns
} from './utils';
import { Selections, ConfigData } from './types';
import { useStyles } from './styled';
import { getSortedList } from 'utils/stringFormat';
import { actions as integrationsActions, selectors as integrationsSelectors, Integration } from 'store/integrations';
import { selectors as clientsSelectors, Client } from 'store/clients';
import { actions as venuesActions, selectors as venuesSelectors, Venue } from 'store/venues';
import { actions as ciActions, selectors as ciSelectors, ClientIntegration } from 'store/clientIntegrations';
import {
  actions as ticketTemplatesActions,
  selectors as ticketTemplatesSelectors,
  TicketTemplate
} from 'store/ticketTemplates';
import { actions as kioskActions, selectors as kiosksSelectors, Kiosk } from 'store/kiosks';
import { actions as configActions, selectors as configSelectors, Configuration } from 'store/configurations';
import { actions as hardwareActions, selectors as hardwareSelectors, Hardware } from 'store/hardware';
import { actions as kioskHardwareActions, KioskHardware } from 'store/kioskHardware';
import { actions as kioskTicketTemplateActions, KioskTicketTemplate } from 'store/kioskTicketTemplates';
import { actions as areasActions, selectors as areasSelectors, Area } from 'store/areas';
import { actions as mediaActions, selectors as mediaSelectors } from 'store/media';
import { Configurations } from './components/ConfigTable';
import { ConfigMapping } from './components/ConfigMappingTable';
import { ConfigCreateOverlay } from './components/ConfigCreateOverlay';
import { ActionOverlay } from 'components/ActionOverlay';
import { getUniqueName } from './utils';

const ConfigurationView: React.FC = () => {
  const [
    getIntegrations,
    createIntegration,
    updateIntegration,
    deleteIntegration,
    resetIntegrationAction,
    resetIntegrationError
  ] = useActions([
    integrationsActions.getIntegrations,
    integrationsActions.createIntegration,
    integrationsActions.updateIntegration,
    integrationsActions.deleteIntegration,
    integrationsActions.resetAction,
    integrationsActions.resetError
  ]);
  const [getVenues, createVenue, updateVenue, deleteVenue, resetVenueAction, resetVenueError] = useActions([
    venuesActions.getVenues,
    venuesActions.createVenue,
    venuesActions.updateVenue,
    venuesActions.deleteVenue,
    venuesActions.resetAction,
    venuesActions.resetError
  ]);
  const [getAreas, createArea, updateArea, deleteArea, resetArea, resetAreaError] = useActions([
    areasActions.getAreas,
    areasActions.createArea,
    areasActions.updateArea,
    areasActions.deleteArea,
    areasActions.resetAreas,
    areasActions.resetError
  ]);
  const [
    getCIs,
    createCI,
    updateCI,
    deleteCI,
    resetCIAction,
    resetCIError,
    resetCustomer,
    resetAPIAccess,
    testAPIAccess
  ] = useActions([
    ciActions.getClientIntegrations,
    ciActions.createClientIntegration,
    ciActions.updateClientIntegration,
    ciActions.deleteClientIntegration,
    ciActions.resetAction,
    ciActions.resetError,
    ciActions.resetCustomer,
    ciActions.resetAPIAccess,
    ciActions.testAPIAccess
  ]);
  const [getConfigs, createConfig, updateConfig, deleteConfig, resetConfigAction, resetConfigError] = useActions([
    configActions.getConfigurations,
    configActions.createConfiguration,
    configActions.updateConfiguration,
    configActions.deleteConfiguration,
    configActions.resetAction,
    configActions.resetError
  ]);
  const [
    getTicketTemplates,
    createTicketTemplate,
    updateTicketTemplate,
    deleteTicketTemplate,
    resetTicketAction,
    resetTicketTemplateError
  ] = useActions([
    ticketTemplatesActions.getTicketTemplates,
    ticketTemplatesActions.createTicketTemplate,
    ticketTemplatesActions.updateTicketTemplate,
    ticketTemplatesActions.deleteTicketTemplate,
    ticketTemplatesActions.resetAction,
    ticketTemplatesActions.resetError
  ]);
  const [getKiosks, copyKiosk, updateKiosk, deleteKiosk, resetKioskAction, resetKioskError] = useActions([
    kioskActions.getKiosks,
    kioskActions.copyKiosk,
    kioskActions.updateKiosk,
    kioskActions.deleteKiosk,
    kioskActions.resetAction,
    kioskActions.resetError
  ]);
  const [getHardware, createHardware, updateHardware, deleteHardware, resetHardwareAction, resetHardwareError] =
    useActions([
      hardwareActions.getHardware,
      hardwareActions.createHardware,
      hardwareActions.updateHardware,
      hardwareActions.deleteHardware,
      hardwareActions.resetAction,
      hardwareActions.resetError
    ]);

  const deleteKioskTicketTemplate = useActions(kioskTicketTemplateActions.deleteKioskTicketTemplate);
  const deleteKioskHardware = useActions(kioskHardwareActions.deleteKioskHardware);

  const [deleteAssociatedMedia, resetMediaAction] = useActions([
    mediaActions.deleteAssociatedMedia,
    mediaActions.resetAction
  ]);

  const integrations = useSelector(integrationsSelectors.selectIntegrations);
  const { error: integrationError, success: integrationSuccess } = useSelector(integrationsSelectors.selectAction);

  const client = useSelector(clientsSelectors.selectClient) as Client;

  const venues = useSelector(venuesSelectors.selectVenues);
  const { error: venuesError, success: venueSuccess } = useSelector(venuesSelectors.selectAction);

  const areas = useSelector(areasSelectors.selectAreas);
  const { error: areaError, success: areasSuccess } = useSelector(areasSelectors.selectAction);

  const clientIntegrations = useSelector(ciSelectors.selectClientIntegrations);
  const customer = useSelector(ciSelectors.selectCustomer);
  const apiAccess = useSelector(ciSelectors.selectAPIAccess);
  const { error: ciError, success: ciSuccess, pending } = useSelector(ciSelectors.selectAction);

  const ticketTemplates = useSelector(ticketTemplatesSelectors.selectTicketTemplates);
  const { error: ticketTemplatesError, success: ttSuccess } = useSelector(ticketTemplatesSelectors.selectAction);

  const kiosks = useSelector(kiosksSelectors.selectKiosks);
  const { error: kioskError, success: kioskSuccess } = useSelector(kiosksSelectors.selectAction);

  const configurations = useSelector(configSelectors.selectConfigurations);
  const { error: configError, success: configSuccess } = useSelector(configSelectors.selectAction);

  const hardware = useSelector(hardwareSelectors.selectHardware);
  const { error: hardwareError, success: hardwareSuccess } = useSelector(hardwareSelectors.selectAction);

  const { error: mediaError, successful: mediaSuccess } = useSelector(mediaSelectors.selectAction);

  const error =
    venuesError ||
    ciError ||
    kioskError ||
    configError ||
    ticketTemplatesError ||
    areaError ||
    integrationError ||
    hardwareError ||
    mediaError;

  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [selectedCI, setSelectedCI] = useState<ClientIntegration | null>(null);
  const [selectedKiosk, setSelectedKiosk] = useState<Kiosk | null>(null);
  const [selectedConfiguration, setConfiguration] = useState<Configuration | null>(null);
  const [selectedHardware, setSelectedHardware] = useState<Hardware | null>(null);
  const [selectedKioskHardware, setSelectedKioskHardware] = useState<KioskHardware | null>(null);
  const [selectedKioskTicketTemplate, setSelectedKioskTicketTemplate] = useState<KioskTicketTemplate | null>(null);
  const [selectedTT, setSelectedTT] = useState<TicketTemplate | null>(null);
  const [selectedArea, setSelectedArea] = useState<Area | null>(null);

  const [tabSelected, setTabSelected] = useState<Selections>(Selections.INTEGRATION);
  const [showCreateOverlay, setShowCreateOverlay] = useState(false);
  const [showDeleteOverlay, setShowDeleteOverlay] = useState(false);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [customerCreateOverlay, setCustomerCreateOverlay] = useState(false);
  const [deleteMediaOverlay, setDeleteMediaOverlay] = useState(false);
  const classes = useStyles()();

  React.useEffect(() => {
    if (client) {
      setSelectedIntegration(null);
      setSelectedVenue(null);
      setSelectedCI(null);
      setSelectedKiosk(null);
      setConfiguration(null);
      setSelectedKioskHardware(null);
      setSelectedKioskTicketTemplate(null);
      setSelectedTT(null);
      setSelectedArea(null);
      getVenues();
      getCIs();
      getKiosks({});
      getConfigs();
      getHardware();
      getIntegrations();
      getTicketTemplates();
      getAreas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  React.useEffect(() => {
    if (error && !showEditDrawer) {
      if (integrationError) resetIntegrationError();
      if (venuesError) resetVenueError();
      if (ciError) resetCIError();
      if (kioskError) resetKioskError();
      if (configError) resetConfigError();
      if (hardwareError) resetHardwareError();
      if (ticketTemplatesError) resetTicketTemplateError();
      if (areaError) resetAreaError();
      if (mediaError) resetMediaAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEditDrawer]);

  // ON UPDATE OF Integration || SUCCESSFUL
  React.useEffect(() => {
    // IF integration EXIST OR SUCCESSFUL ACTION - GET integrations
    if (integrationSuccess) {
      setSelectedIntegration(null);
      resetIntegrationAction();
      getIntegrations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationSuccess]);

  React.useEffect(() => {
    if (venueSuccess) {
      setSelectedVenue(null);
      getVenues();
      resetVenueAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueSuccess]);

  React.useEffect(() => {
    if (hardwareSuccess) {
      setSelectedHardware(null);
      getHardware();
      resetHardwareAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hardwareSuccess]);

  React.useEffect(() => {
    if (ttSuccess) {
      setSelectedTT(null);
      getTicketTemplates();
      resetTicketAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ttSuccess]);

  React.useEffect(() => {
    if (areasSuccess) {
      setSelectedArea(null);
      getAreas();
      resetArea();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areasSuccess]);

  React.useEffect(() => {
    if (ciSuccess) {
      setSelectedCI(null);
      getCIs();
      resetCIAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ciSuccess]);

  React.useEffect(() => {
    if (kioskSuccess) {
      setSelectedKiosk(null);
      getKiosks({});
      resetKioskAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kioskSuccess]);

  React.useEffect(() => {
    if (configSuccess) {
      setConfiguration(null);
      getConfigs();
      resetConfigAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configSuccess]);

  React.useEffect(() => {
    if (mediaSuccess) {
      resetMediaAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaSuccess]);

  React.useEffect(() => {
    if (selectedIntegration) {
      const updatedIntegration = integrations.find((integ) => integ.id === selectedIntegration.id);
      setSelectedIntegration(updatedIntegration || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrations]);

  React.useEffect(() => {
    if (selectedVenue) {
      const updatedVenue = venues.find((v) => v.id === selectedVenue.id);
      setSelectedVenue(updatedVenue || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venues]);

  React.useEffect(() => {
    if (selectedHardware) {
      const updatedHardware = hardware.find((h) => h.id === selectedHardware.id);
      setSelectedHardware(updatedHardware || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hardware]);

  React.useEffect(() => {
    if (selectedTT) {
      const updatedTT = ticketTemplates.find((v) => v.id === selectedTT.id);
      setSelectedTT(updatedTT || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketTemplates]);

  React.useEffect(() => {
    if (selectedArea) {
      const updatedArea = areas.find((a) => a.id === selectedArea.id);
      setSelectedArea(updatedArea || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areas]);

  React.useEffect(() => {
    if (selectedCI) {
      const updatedCI = clientIntegrations.find((ci) => ci.id === selectedCI.id);
      setSelectedCI(updatedCI || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientIntegrations]);

  React.useEffect(() => {
    if (selectedKiosk) {
      const updatedKiosk = kiosks.find((k) => k.id === selectedKiosk.id);
      if (updatedKiosk) {
        const cI = clientIntegrations.find((ci) => ci.id === updatedKiosk.clientIntegrationId);
        const configuration = configurations.find((c) => c.id === updatedKiosk.configurationId);
        const area = areas.find((a) => a.id === updatedKiosk.areaId);
        updatedKiosk.clientIntegrations = cI;
        updatedKiosk.configurations = configuration;
        updatedKiosk.area = area;
        setSelectedKiosk(updatedKiosk);
      } else {
        setSelectedKiosk(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kiosks]);

  React.useEffect(() => {
    if (selectedConfiguration) {
      const updatedConfig = configurations.find((c) => c.id === selectedConfiguration.id);
      setConfiguration(updatedConfig || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configurations]);

  const onDeleteConfirmClick = (item: string) => {
    let payload;
    if (item === 'venue') {
      payload = { id: selectedVenue && selectedVenue.id };
      deleteVenue(payload);
    } else if (item === 'clientIntegration') {
      payload = { id: selectedCI && selectedCI.id };
      deleteCI(payload);
    } else if (item === 'kiosk') {
      payload = { id: selectedKiosk && selectedKiosk.id };
      deleteKiosk(payload);
    } else if (item === 'hardware') {
      payload = { id: selectedHardware && selectedHardware.id };
      deleteHardware(payload);
    } else if (item === 'configuration') {
      payload = { id: selectedConfiguration && selectedConfiguration.id };
      deleteConfig(payload);
    } else if (item === 'integration') {
      payload = { id: selectedIntegration && selectedIntegration.id };
      deleteIntegration(payload);
    } else if (item === 'ticketTemplate') {
      payload = { id: selectedTT && selectedTT.id };
      deleteTicketTemplate(payload);
    } else if (item === 'area') {
      payload = { id: selectedArea && selectedArea.id };
      deleteArea(payload);
    }

    setShowDeleteOverlay(false);
  };

  const onCopyConfirmClick = (item: string) => {
    let payload;
    if (item === 'venue') {
      if (selectedVenue) {
        payload = {
          name: getUniqueName(selectedVenue.name, venues),
          address: selectedVenue.address ? selectedVenue.address : undefined
        };
        createVenue(payload);
      }
    } else if (item === 'clientIntegration') {
      if (selectedCI) {
        payload = {
          name: getUniqueName(selectedCI.name, clientIntegrations),
          baseUrl: selectedCI.baseUrl,
          integrationId: selectedCI.integration.id,
          configuration: selectedCI.configuration,
          cityPass: selectedCI.cityPass
        };
        createCI(payload);
      }
    } else if (item === 'hardware') {
      if (selectedHardware) {
        payload = {
          name: getUniqueName(selectedHardware.name, hardware),
          code: selectedHardware.code,
          type: selectedHardware.type,
          details: selectedHardware.details
        };
        createHardware(payload);
      }
    } else if (item === 'kiosk') {
      if (selectedKiosk) {
        copyKiosk({ id: selectedKiosk.id });
      }
    } else if (item === 'configuration') {
      if (selectedConfiguration) {
        payload = {
          name: getUniqueName(selectedConfiguration.name, configurations),
          ticketing: selectedConfiguration.ticketing,
          willCall: selectedConfiguration.willCall,
          messaging: selectedConfiguration.messaging,
          infoView: selectedConfiguration.infoView,
          configuration: selectedConfiguration.configuration,
          cityPass: selectedConfiguration.cityPass
        };
        createConfig(payload);
      }
    } else if (item === 'integration') {
      if (selectedIntegration) {
        const newName = getUniqueName(selectedIntegration.name, integrations);
        payload = {
          name: newName,
          code: newName
        };
        createIntegration(payload);
      }
    } else if (item === 'ticketTemplate') {
      if (selectedTT) {
        payload = {
          type: selectedTT.type,
          template: selectedTT.template
        };
        createTicketTemplate(payload);
      }
    } else if (item === 'area') {
      if (selectedArea) {
        payload = {
          name: getUniqueName(selectedArea.name, areas),
          description: selectedArea.description ? selectedArea.description : undefined,
          venueId: selectedArea.venueId
        };
        createArea(payload);
      }
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onDeleteConfigConfirm = (configType: string, payload: any) => {
    if (configType === 'venue') {
      updateVenue(payload);
    } else if (configType === 'clientIntegration') {
      updateCI(payload);
    } else if (configType === 'kiosk') {
      updateKiosk(payload);
    } else if (configType === 'configuration') {
      updateConfig(payload);
    } else if (configType === 'hardware') {
      updateHardware(payload);
    } else if (configType === 'kioskHardware') {
      deleteKioskHardware(payload);
    } else if (configType === 'integration') {
      updateIntegration(payload);
    } else if (configType === 'ticketTemplate') {
      updateTicketTemplate(payload);
    } else if (configType === 'kioskTicketTemplate') {
      deleteKioskTicketTemplate(payload);
    } else if (configType === 'area') {
      updateArea(payload);
    }
  };

  const deleteOverlayTitle = (item: string) => {
    let title;
    if (item === 'venue') {
      title = selectedVenue && selectedVenue.name;
    } else if (item === 'clientIntegration') {
      title = selectedCI && selectedCI.name;
    } else if (item === 'kiosk') {
      title = selectedKiosk && selectedKiosk.name;
    } else if (item === 'configuration') {
      title = selectedConfiguration && selectedConfiguration.name;
    } else if (item === 'hardware') {
      title = selectedHardware && selectedHardware.name;
    } else if (item === 'kioskHardware') {
      title = selectedKioskHardware && selectedKioskHardware.id;
    } else if (item === 'integration') {
      title = selectedIntegration && selectedIntegration.name;
    } else if (item === 'ticketTemplate') {
      title = selectedTT && selectedTT.name;
    } else if (item === 'kioskTicketTemplate') {
      title = selectedKioskTicketTemplate && selectedKioskTicketTemplate.id;
    } else if (item === 'area') {
      title = selectedArea && selectedArea.name;
    }

    return title;
  };

  const resetError = () => {
    if (error) {
      if (venuesError) {
        resetVenueError();
      } else if (areaError) {
        resetAreaError();
      } else if (ciError) {
        resetCIError();
      } else if (kioskError) {
        resetKioskError();
      } else if (configError) {
        resetConfigError();
      } else if (hardwareError) {
        resetHardwareError();
      } else if (integrationError) {
        resetIntegrationError();
      } else if (ticketTemplatesError) {
        resetTicketTemplateError();
      } else if (mediaError) {
        resetMediaAction();
      }
    }
  };

  const handleDeleteMedia = () => {
    if (Selections.CLIENT_INTEGRATION === tabSelected && selectedCI) {
      deleteAssociatedMedia({ clientIntegrationId: selectedCI.id });
    } else if (Selections.CONFIGURATION === tabSelected && selectedConfiguration) {
      deleteAssociatedMedia({ configurationId: selectedConfiguration.id });
    }
    setDeleteMediaOverlay(false);
  };

  const getDeleteMediaTitle = (): string => {
    let deleteTitle = 'Are you sure you want to delete all media associated with the';
    if (Selections.CLIENT_INTEGRATION === tabSelected && selectedCI) {
      deleteTitle = `${deleteTitle} ${selectedCI.name} client integration?`;
    } else if (Selections.CONFIGURATION === tabSelected && selectedConfiguration) {
      deleteTitle = `${deleteTitle} ${selectedConfiguration.name} configuration?`;
    }

    return deleteTitle;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ConfigSelector = (props: { configData: ConfigData[]; selectedConfig: ConfigData | null; setConfig?: any }) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onNameChange = (item: any): any => {
      if (tabSelected === 'kiosk') {
        const cIntegration = clientIntegrations.find((ci) => ci.id === item.clientIntegrationId);
        const config = configurations.find((c) => c.id === item.configurationId);
        const area = areas.find((a) => a.id === item.areaId);
        item.clientIntegrations = cIntegration;
        item.configurations = config;
        item.area = area;
      }

      return props.setConfig(item);
    };

    const getDisplayName = (item: ConfigData): string => {
      let name = (item && item.name) || '';
      if (tabSelected === Selections.TICKET_TEMPLATE) {
        name = item && `${item.id} (${(item as TicketTemplate).type})`;
      }

      return name;
    };

    const getSortedItems = (items: ConfigData[]): ConfigData[] => {
      if (tabSelected === Selections.TICKET_TEMPLATE) {
        return getSortedList(items, 'type', 'id');
      }
      return getSortedList(items, 'name');
    };

    return (
      <>
        <FlexBox fullWidth direction="row" sx={{ width: '100%' }} margin="5 5 0" align="center">
          <FlexBox direction="column" justify="center" align="center">
            <FlexBox direction="row" align="center">
              <Typography fontSize={24} fontWeight={700}>
                Name
              </Typography>
              <Box width={60} />
              <SelectDropdown
                items={getSortedItems(props.configData)}
                itemToString={(item: ConfigData) => getDisplayName(item)}
                selectedItem={props.selectedConfig as ConfigData}
                onChange={(item: ConfigData) => onNameChange(item)}
                data-testid="config-selector"
              />
            </FlexBox>
            {((Selections.CLIENT_INTEGRATION === tabSelected && selectedCI) ||
              (Selections.CONFIGURATION === tabSelected && selectedConfiguration)) && (
              <FlexBox sx={{ height: '60px' }} />
            )}
          </FlexBox>

          <FlexBox direction="column" justify="center" align="center">
            <FlexBox direction="row" padding="2 4" gutter={2}>
              <>
                <Button
                  startIconName="plus_circle"
                  sx={{ minWidth: '145px' }}
                  onClick={() => setShowCreateOverlay(true)}
                >
                  Create
                </Button>
                <Button
                  startIconName="exit_circle"
                  sx={{ minWidth: '145px' }}
                  appearance="white"
                  onClick={() => setShowDeleteOverlay(true)}
                >
                  Delete
                </Button>
                <Button sx={{ minWidth: '145px' }} appearance="white" onClick={() => onCopyConfirmClick(tabSelected)}>
                  Copy
                </Button>
              </>
            </FlexBox>
            {Selections.CLIENT_INTEGRATION === tabSelected && selectedCI && (
              <FlexBox fullWidth padding="2" gutter={2} justify="start">
                <Button sx={{ minWidth: '145px' }} appearance="white" onClick={() => testAPIAccess(selectedCI.id)}>
                  Test Access
                </Button>
                {selectedCI.integration.code === 'tessitura' && (
                  <Button
                    startIconName="plus_circle"
                    sx={{ minWidth: '145px' }}
                    appearance="white"
                    onClick={() => setCustomerCreateOverlay(true)}
                  >
                    Customer
                  </Button>
                )}
                <Button
                  style={{ minWidth: '150px' }}
                  appearance="white"
                  startIconName="exit_circle"
                  onClick={() => setDeleteMediaOverlay(true)}
                >
                  Delete Media
                </Button>
              </FlexBox>
            )}
            {Selections.CONFIGURATION === tabSelected && selectedConfiguration && (
              <FlexBox fullWidth padding="2" gutter={2} justify="start">
                <Button
                  sx={{
                    minWidth: '150px'
                  }}
                  appearance="white"
                  startIconName="exit_circle"
                  onClick={() => setDeleteMediaOverlay(true)}
                >
                  Delete Media
                </Button>
              </FlexBox>
            )}
          </FlexBox>
        </FlexBox>
      </>
    );
  };

  const MappingSelector = () => (
    <FlexBox fullWidth direction="row" margin="5 5 0" align="center" sx={{ width: '100%' }}>
      {[Selections.KIOSK_HARDWARE].includes(tabSelected) && (
        <>
          <ConfigMapping
            kiosks={kiosks}
            mappingData={hardware}
            mappingType="kioskHardware"
            columns={kioskHardwareColumns}
            onDeleteConfirm={onDeleteConfigConfirm}
          />
        </>
      )}
      {[Selections.KIOSK_TICKET_TEMPLATE].includes(tabSelected) && (
        <>
          <ConfigMapping
            kiosks={kiosks}
            mappingData={ticketTemplates}
            mappingType="kioskTicketTemplate"
            columns={kioskTicketTemplateColumns}
            onDeleteConfirm={onDeleteConfigConfirm}
          />
        </>
      )}
    </FlexBox>
  );

  return (
    <>
      {pending && (
        <Backdrop open={pending ? true : false}>
          <DotProgress color={color.LIGHT_GRAY} />
        </Backdrop>
      )}
      {/* HEADER ROW */}
      <Grid>
        <FlexBox direction="column" padding="7 7 0" justify="between">
          <Typography className={classes.configHeaderText}>Configuration</Typography>
          <FlexBox align="center" margin="5 0 4" direction="row" maxWidth="100%">
            <MuiBox className={classes.configSelectorPills}>
              <Selector onChange={(_, v) => setTabSelected(v as Selections)} value={tabSelected} size="small">
                <Selector.Item value={Selections.INTEGRATION} label="Integrations" />
                <Selector.Item value={Selections.HARDWARE} label="Hardware" />
              </Selector>
              <Selector onChange={(_, v) => setTabSelected(v as Selections)} value={tabSelected} size="small">
                <Selector.Item value={Selections.CLIENT_INTEGRATION} label="Client Integrations" />
                <Selector.Item value={Selections.VENUE} label="Venues" />
                <Selector.Item value={Selections.AREA} label="Areas" />
                <Selector.Item value={Selections.TICKET_TEMPLATE} label="Ticket Templates" />
                <Selector.Item value={Selections.CONFIGURATION} label="Configurations" />
                <Selector.Item value={Selections.KIOSK} label="Kiosks" />
                <Selector.Item value={Selections.KIOSK_TICKET_TEMPLATE} label="Kiosk Ticket Templates" />
                <Selector.Item value={Selections.KIOSK_HARDWARE} label="Kiosk Hardware" />
              </Selector>
            </MuiBox>

            {[Selections.INTEGRATION].includes(tabSelected) && (
              <ConfigSelector
                configData={integrations}
                selectedConfig={selectedIntegration}
                setConfig={(i: Integration) => setSelectedIntegration(i)}
              />
            )}

            {[Selections.VENUE].includes(tabSelected) && (
              <ConfigSelector
                configData={venues}
                selectedConfig={selectedVenue}
                setConfig={(v: Venue) => setSelectedVenue(v)}
              />
            )}

            {[Selections.AREA].includes(tabSelected) && (
              <ConfigSelector
                configData={areas}
                selectedConfig={selectedArea as Area}
                setConfig={(a: Area) => setSelectedArea(a)}
              />
            )}

            {[Selections.CLIENT_INTEGRATION].includes(tabSelected) && (
              <ConfigSelector
                configData={clientIntegrations}
                selectedConfig={selectedCI}
                setConfig={(ci: ClientIntegration) => setSelectedCI(ci)}
              />
            )}

            {[Selections.TICKET_TEMPLATE].includes(tabSelected) && (
              <ConfigSelector
                configData={ticketTemplates}
                selectedConfig={selectedTT}
                setConfig={(tt: TicketTemplate) => setSelectedTT(tt)}
              />
            )}

            {[Selections.KIOSK].includes(tabSelected) && (
              <ConfigSelector
                configData={kiosks}
                selectedConfig={selectedKiosk}
                setConfig={(kiosk: Kiosk) => setSelectedKiosk(kiosk)}
              />
            )}

            {[Selections.HARDWARE].includes(tabSelected) && (
              <ConfigSelector
                configData={hardware}
                selectedConfig={selectedHardware}
                setConfig={(h: Hardware) => setSelectedHardware(h)}
              />
            )}

            {[Selections.CONFIGURATION].includes(tabSelected) && (
              <ConfigSelector
                configData={configurations}
                selectedConfig={selectedConfiguration}
                setConfig={(config: Configuration) => setConfiguration(config)}
              />
            )}

            {[Selections.KIOSK_TICKET_TEMPLATE, Selections.KIOSK_HARDWARE].includes(tabSelected) && <MappingSelector />}
          </FlexBox>
          {/* </Box> */}
        </FlexBox>
        <FlexBox padding="0 6 6" noWrap>
          {tabSelected === Selections.INTEGRATION && selectedIntegration && (
            <Configurations
              configData={selectedIntegration}
              configType="integration"
              columns={integrationColumns}
              onDeleteConfirm={onDeleteConfigConfirm}
              setDrawerVisibility={setShowEditDrawer}
            />
          )}
          {tabSelected === Selections.VENUE && selectedVenue && (
            <Configurations
              configData={selectedVenue}
              configType="venue"
              columns={venueColumns}
              onDeleteConfirm={onDeleteConfigConfirm}
              setDrawerVisibility={setShowEditDrawer}
            />
          )}
          {tabSelected === Selections.AREA && selectedArea && (
            <Configurations
              configData={selectedArea}
              configType="area"
              columns={areaColumns}
              onDeleteConfirm={onDeleteConfigConfirm}
              setDrawerVisibility={setShowEditDrawer}
            />
          )}
          {tabSelected === Selections.CLIENT_INTEGRATION && selectedCI && (
            <Configurations
              configData={selectedCI}
              configType="clientIntegration"
              columns={clientIntegrationColumns}
              onDeleteConfirm={onDeleteConfigConfirm}
              setDrawerVisibility={setShowEditDrawer}
            />
          )}
          {tabSelected === Selections.TICKET_TEMPLATE && selectedTT && (
            <Configurations
              configData={selectedTT}
              configType="ticketTemplate"
              columns={ticketTemplateColumns}
              onDeleteConfirm={onDeleteConfigConfirm}
              setDrawerVisibility={setShowEditDrawer}
            />
          )}
          {tabSelected === Selections.KIOSK && selectedKiosk && (
            <Configurations
              configData={selectedKiosk}
              configType="kiosk"
              columns={kioskColumns}
              onDeleteConfirm={onDeleteConfigConfirm}
              setDrawerVisibility={setShowEditDrawer}
            />
          )}

          {tabSelected === Selections.HARDWARE && selectedHardware && (
            <Configurations
              configData={selectedHardware}
              configType="hardware"
              columns={hardwareColumns}
              onDeleteConfirm={onDeleteConfigConfirm}
              setDrawerVisibility={setShowEditDrawer}
            />
          )}

          {tabSelected === Selections.CONFIGURATION && selectedConfiguration && (
            <Configurations
              configData={selectedConfiguration}
              configType="configuration"
              columns={configurationColumns}
              onDeleteConfirm={onDeleteConfigConfirm}
              setDrawerVisibility={setShowEditDrawer}
            />
          )}
        </FlexBox>
      </Grid>

      {showCreateOverlay && (
        <ConfigCreateOverlay
          configType={tabSelected}
          onCancel={() => {
            resetError();
            setShowCreateOverlay(false);
          }}
        />
      )}

      {customerCreateOverlay && selectedCI && (
        <ConfigCreateOverlay
          configType="customer"
          selectedCI={selectedCI}
          onCancel={() => {
            resetError();
            setCustomerCreateOverlay(false);
          }}
        />
      )}

      {customer && customer.customerId && (
        <ActionOverlay
          title={
            <Typography className={classes.configDeleteHeader} sx={{ lineHeight: 1, fontWeight: 600 }}>
              Customer {customer.isCreated ? 'created' : 'found'}: {customer.customerId}
            </Typography>
          }
          confirmText="Close"
          onConfirm={() => resetCustomer()}
          width={700}
          height={350}
        />
      )}

      {apiAccess && (
        <ActionOverlay
          title={
            <Typography className={classes.configDeleteHeader} sx={{ lineHeight: 1, fontWeight: 600 }}>
              Success!
            </Typography>
          }
          confirmText="Close"
          onConfirm={() => resetAPIAccess()}
          width={700}
          height={350}
        />
      )}

      {showDeleteOverlay && (
        <ActionOverlay
          title={
            <MuiBox className={classes.configDeleteHeading}>
              <Typography className={classes.configDeleteHeader} sx={{ lineHeight: 1, fontWeight: 600 }}>
                Are you sure you want to delete {deleteOverlayTitle(tabSelected)}?
              </Typography>
            </MuiBox>
          }
          description={
            <MuiBox className={classes.configDeleteDescription}>
              <Typography className={classes.configDeleteDescriptionText}>
                {deleteOverlayTitle(tabSelected)} will delete immediately
              </Typography>
            </MuiBox>
          }
          confirmText="Delete"
          onConfirm={() => onDeleteConfirmClick(tabSelected)}
          cancelText="Back"
          onCancel={() => setShowDeleteOverlay(false)}
          width={800}
          appearance="error"
        />
      )}

      {deleteMediaOverlay && (
        <ActionOverlay
          title={
            <MuiBox className={classes.configDeleteHeading}>
              <Typography className={classes.configDeleteHeader} sx={{ lineHeight: 1, fontWeight: 600 }}>
                {getDeleteMediaTitle()}
              </Typography>
            </MuiBox>
          }
          confirmText="Delete"
          onConfirm={() => handleDeleteMedia()}
          cancelText="Back"
          onCancel={() => setDeleteMediaOverlay(false)}
          width={800}
          appearance="error"
        />
      )}

      {error && !showCreateOverlay && !showEditDrawer && (
        <ActionOverlay
          title={
            <MuiBox className={classes.configDeleteHeading}>
              <Typography
                className={classes.configDeleteHeader}
                sx={{ lineHeight: 1, fontWeight: 600, color: '#C40B0F!important' }}
              >
                {error}
              </Typography>
            </MuiBox>
          }
          confirmText="Close"
          onConfirm={resetError}
          width={800}
          height={350}
          appearance="error"
        />
      )}
    </>
  );
};

export default ConfigurationView;
